import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Aviso, Botao, CampoDeSelecao, Modal } from './ui';
import {
  criarVariaveis,
  mesmaFonte,
  reconhecerVariaveis,
  type FonteDaVariavel,
  type OpcaoDeFonte,
  type UsoDoModelo,
  type VariavelReconhecida,
} from '../servicos/modelosDeContrato';
import { ErroDaApi } from '../paginas/ErroDaApi';

/** O que se escolheu para cada variável: uma fonte, ou não criar. */
type Escolha = FonteDaVariavel | 'NAO_CRIAR';

/**
 * As variáveis novas de um modelo, confirmadas antes de salvar (D-022).
 *
 * Para cada `[VARIAVEL]` que ainda não existe, o Deca mostra o que entendeu
 * do nome — já marcado — e as informações parecidas; "outra informação" abre
 * a lista inteira do uso. Confirmada, a variável passa a valer em todos os
 * modelos onde aquela parte (ou aquele dado) existe. "Não criar" deixa como
 * está: ela some do PDF.
 *
 * A decisão de 29/10/2026 é sempre confirmar, mesmo quando o nome diz tudo.
 */
export function ConfirmarVariaveis(props: {
  readonly tokens: readonly string[];
  /**
   * Onde a variável vai valer: o uso de um modelo de contrato, ou um e-mail
   * (D-022, parte 2) — cada um com a sua leitura e a sua gravação.
   */
  readonly onde:
    | { readonly contrato: UsoDoModelo }
    | {
        readonly email: string;
        readonly reconhecer: (
          tokens: readonly string[],
        ) => Promise<{ variaveis: VariavelReconhecida[]; todas: OpcaoDeFonte[] }>;
        readonly criar: (
          lista: readonly { readonly token: string; readonly fonte: FonteDaVariavel }[],
        ) => Promise<unknown>;
      };
  /** Depois de criar (ou de decidir não criar nenhuma): salvar segue daqui. */
  readonly aoConcluir: () => void;
  readonly aoCancelar: () => void;
  /** O texto do botão principal: "Confirmar e salvar", ou só "Criar variáveis". */
  readonly rotuloConfirmar: string;
}): JSX.Element {
  const { onde } = props;
  const reconhecimento = useQuery({
    queryKey: [
      'variaveis-reconhecimento',
      'contrato' in onde ? onde.contrato : onde.email,
      props.tokens,
    ],
    queryFn: () =>
      'contrato' in onde
        ? reconhecerVariaveis(onde.contrato, props.tokens)
        : onde.reconhecer(props.tokens),
  });
  /** Como a variável se escreve no texto: `[X]` no contrato, `{{x}}` no e-mail. */
  const escrita = (token: string): string => ('contrato' in onde ? `[${token}]` : `{{${token}}}`);
  const [escolhas, setEscolhas] = useState<Record<string, Escolha>>({});

  // A sugestão do Deca já vem marcada; sem nenhuma, "não criar" — a pessoa
  // escolhe na lista.
  useEffect(() => {
    if (reconhecimento.data === undefined) return;
    setEscolhas(
      Object.fromEntries(
        reconhecimento.data.variaveis
          .filter((v) => !v.jaExiste)
          .map((v) => [v.token, v.provavel?.fonte ?? 'NAO_CRIAR']),
      ),
    );
  }, [reconhecimento.data]);

  const criar = useMutation({
    mutationFn: async () => {
      const lista = Object.entries(escolhas)
        .filter((e): e is [string, FonteDaVariavel] => e[1] !== 'NAO_CRIAR')
        .map(([token, fonte]) => ({ token, fonte }));
      if (lista.length === 0) return;
      if ('contrato' in onde) await criarVariaveis(lista);
      else await onde.criar(lista);
    },
    onSuccess: props.aoConcluir,
  });

  const dados = reconhecimento.data;
  const novas = dados?.variaveis.filter((v) => !v.jaExiste) ?? [];

  // Todas já existem (criadas em outro modelo): nada a perguntar.
  useEffect(() => {
    if (dados !== undefined && novas.length === 0) props.aoConcluir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  return (
    <Modal
      aberto
      aoFechar={props.aoCancelar}
      titulo="Variáveis novas neste modelo"
      descricao="Diga que informação cada uma traz. Confirmada, ela fica disponível nos próximos modelos também."
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoCancelar}>
            Voltar ao texto
          </Botao>
          <Botao
            disabled={dados === undefined}
            carregando={criar.isPending}
            onClick={() => criar.mutate()}
          >
            {props.rotuloConfirmar}
          </Botao>
        </>
      }
    >
      {reconhecimento.isError ? (
        <ErroDaApi titulo="Não foi possível ler as variáveis" erro={reconhecimento.error} />
      ) : dados === undefined ? (
        <p className="py-6 text-center text-sm text-conteudo-sutil">Lendo as variáveis…</p>
      ) : (
        <div className="flex flex-col gap-5">
          {novas.map((v) => {
            const escolha = escolhas[v.token] ?? 'NAO_CRIAR';
            const nasOpcoes =
              escolha === 'NAO_CRIAR' || v.sugestoes.some((s) => mesmaFonte(s.fonte, escolha));
            return (
              <fieldset
                key={v.token}
                className="flex flex-col gap-2 rounded-card border border-borda p-4"
              >
                <legend className="px-1 font-mono text-sm text-acento-ciano">
                  {escrita(v.token)}
                </legend>
                <p className="text-xs text-conteudo-secundario">
                  {v.provavel === null
                    ? 'O Deca não entendeu pelo nome. Escolha a informação que ela deve trazer:'
                    : 'Pelo nome, o Deca entendeu assim — confirme ou troque:'}
                </p>
                <div className="flex flex-col gap-1">
                  {v.sugestoes.map((s, i) => (
                    <label
                      key={i}
                      className="flex cursor-pointer items-baseline gap-2 rounded-card px-2 py-1 text-sm hover:bg-superficie-vidro"
                    >
                      <input
                        type="radio"
                        name={v.token}
                        checked={escolha !== 'NAO_CRIAR' && mesmaFonte(s.fonte, escolha)}
                        onChange={() => setEscolhas((e) => ({ ...e, [v.token]: s.fonte }))}
                      />
                      <span className="text-conteudo-primario">{s.rotulo}</span>
                      {v.provavel !== null && mesmaFonte(s.fonte, v.provavel.fonte) && (
                        <span className="text-xs text-economia">sugestão do Deca</span>
                      )}
                      <span className="text-xs text-conteudo-sutil">ex.: {s.exemplo}</span>
                    </label>
                  ))}
                  <label className="flex cursor-pointer items-baseline gap-2 rounded-card px-2 py-1 text-sm hover:bg-superficie-vidro">
                    <input
                      type="radio"
                      name={v.token}
                      checked={escolha === 'NAO_CRIAR'}
                      onChange={() => setEscolhas((e) => ({ ...e, [v.token]: 'NAO_CRIAR' }))}
                    />
                    <span className="text-conteudo-secundario">
                      {'contrato' in onde
                        ? 'Não criar — fica no texto e some do PDF'
                        : 'Não criar — o e-mail fica com pendência e não pode ser ligado'}
                    </span>
                  </label>
                </div>
                <CampoDeSelecao
                  rotulo="Outra informação"
                  value={nasOpcoes ? '' : JSON.stringify(escolha)}
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (valor === '') return;
                    setEscolhas((atual) => ({
                      ...atual,
                      [v.token]: JSON.parse(valor) as FonteDaVariavel,
                    }));
                  }}
                  opcoes={[
                    { valor: '', rotulo: 'Escolher da lista completa…' },
                    ...dados.todas.map((t) => ({
                      valor: JSON.stringify(t.fonte),
                      rotulo: t.rotulo,
                    })),
                  ]}
                />
              </fieldset>
            );
          })}
          {dados.variaveis.some((v) => v.jaExiste) && (
            <Aviso tom="informacao">
              {dados.variaveis
                .filter((v) => v.jaExiste)
                .map((v) => escrita(v.token))
                .join(', ')}{' '}
              já existem (criadas em outro modelo) e não precisam de confirmação.
            </Aviso>
          )}
          {criar.isError && <ErroDaApi titulo="Não foi possível criar" erro={criar.error} />}
        </div>
      )}
    </Modal>
  );
}
